import { useEffect, useState, useRef } from 'react';
import { useIsMutating, useQueryClient } from 'react-query';
import { PRESENTATIONS } from '../../constants/endpoints';
import useApi from '../../api/hooks/useApi';
import useLiveEvent from '../../hooks/useLiveEvent';
import uuid from '../../utils/uuid';

export const EventTypes = {
  SESSION_STARTED: 'Presentation:SessionStarted',
  SESSION_UPDATED: 'Presentation:SessionUpdated',
  SESSION_ENDED: 'Presentation:SessionEnded',
  REACTIONS_UPDATED: 'Presentation:ReactionsUpdated',
};

const useLivePresentation = (spaceId, config) => {
  const [correlationId, setCorrelationId] = useState(uuid());
  const [enabled, setEnabled] = useState(false);
  // we need this to disable the query if a mutation that could affect it is currently running
  const isMutating = useIsMutating();
  const queryClient = useQueryClient();
  const initialRender = useRef(true);

  useEffect(() => {
    if (spaceId && !isMutating) {
      setEnabled(true);
    }
  }, [spaceId, isMutating]);

  const url = `${PRESENTATIONS()}/${spaceId}/presentations?live=true`;
  const { data, refetch, isLoading, isError, isFetched } = useApi(
    {
      url,
    },
    {
      ...config,
      enabled,
    }
  );

  // assume we only have 1 live presentation at a time.
  const presentation = data?.[0];
  const presentationId = presentation?.id;
  const currentSession = presentation?.sessions?.find(
    (session) => !session.stoppedAt
  );
  const pdfFile = presentation?.files?.find(
    (file) => file.mediaType === 'application/pdf'
  );

  const sessionUpdatedEvent = useLiveEvent(EventTypes.SESSION_UPDATED);
  const sessionStartedEvent = useLiveEvent(EventTypes.SESSION_STARTED);
  const sessionEndedEvent = useLiveEvent(EventTypes.SESSION_ENDED);

  useEffect(() => {
    // don't refetch on initial render
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    // don't refetch if none of these events have been fired
    if (!sessionUpdatedEvent && !sessionStartedEvent && !sessionEndedEvent) {
      return;
    }

    if (!isMutating) {
      refetch();
    }
  }, [
    enabled,
    sessionUpdatedEvent,
    sessionStartedEvent,
    sessionEndedEvent,
    refetch,
    isMutating,
  ]);

  const reactionsUpdatedEvent = useLiveEvent(EventTypes.REACTIONS_UPDATED);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (reactionsUpdatedEvent && reactionsUpdatedEvent.id === presentationId) {
      queryClient.setQueryData(url, [reactionsUpdatedEvent]);
    }
  }, [enabled, reactionsUpdatedEvent, queryClient, url, presentationId]);

  useEffect(() => {
    if (!sessionStartedEvent) {
      return;
    }

    setCorrelationId(sessionStartedEvent.correlationId);
  }, [sessionStartedEvent]);

  return {
    isFetched,
    isError,
    isLoading,
    presentation,
    currentSession,
    documentUrl: pdfFile?.src,
    slides: presentation?.slides,
    currentSlide: currentSession?.currentSlide,
    revealStats: currentSession?.revealStats,
    revealCorrect: currentSession?.revealCorrect,
    correlationId,
    refetch,
  };
};

export default useLivePresentation;
